<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Banks</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Banks</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center">
              <h1 class="mb-0">Banks</h1>
            </div>

            <div class="form-body">
              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search banks..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading banks...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Bank Name</th>
                        <th>Bank Code</th>
                        <th>Sort Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="4" class="text-center text-muted">No banks to display.</td>
                      </tr>
                      <tr v-for="(bank, index) in paginatedItems" :key="bank.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">
                          {{ (currentPage - 1) * pageLength + index + 1 }}
                        </td>
                        <td data-label="Bank Name" class="primary-col fw-semibold" title="View Bank">
                          <router-link :to="{ name: 'ViewBank', params: { id: bank.id } }"
                            class="view-link fw-semibold text-decoration-underline">
                            {{ bank.bankName }}
                          </router-link>
                        </td>
                        <td data-label="Bank Code" class="secondary-col">{{ bank.bankCodeName }}</td>
                        <td data-label="Sort Code" class="secondary-col">{{ bank.bankSortCode }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in getPageNumbers" :key="page" class="page-item"
                      :class="{ active: currentPage === page, disabled: page === '...' }">
                      <button class="page-link" @click.prevent="page !== '...' && changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const banks = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const fetchBanks = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/payment/banks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    banks.value = response.data.map(bank => ({
      id: bank.id,
      bankName: bank.bankName ?? '',
      bankCodeName: bank.bankCodeName ?? '',
      bankSortCode: bank.bankSortCode ?? '',
    }))
  } catch (error) {
    console.error('Failed to fetch banks:', error)
    banks.value = []
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  if (!debouncedSearch.value) return banks.value
  const term = debouncedSearch.value.toLowerCase()
  return banks.value.filter(bank =>
    bank.bankName.toLowerCase().includes(term) ||
    bank.bankCodeName.toLowerCase().includes(term) ||
    bank.bankSortCode.toLowerCase().includes(term)
  )
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => Math.ceil(filteredItems.value.length / pageLength.value))

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) currentPage.value = page
}

const getPageNumbers = computed(() => {
  const pages = []
  const delta = 2
  const total = totalPages.value
  const current = currentPage.value

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i)
    return pages
  }

  pages.push(1)
  if (current > delta + 2) pages.push('...')

  const start = Math.max(2, current - delta)
  const end = Math.min(total - 1, current + delta)
  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - delta - 1) pages.push('...')
  pages.push(total)

  return pages
})

const exportToPDF = () => {
  const doc = new jsPDF()
  doc.setFontSize(16)
  doc.text('Banks', 14, 15)
  doc.setFontSize(10)
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)
  autoTable(doc, {
    startY: 30,
    head: [['S/N', 'Bank Name', 'Bank Code', 'Sort Code']],
    body: filteredItems.value.map((bank, index) => [
      index + 1,
      bank.bankName,
      bank.bankCodeName,
      bank.bankSortCode,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] },
  })
  doc.save('banks.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Banks')
  worksheet.addRow(['S/N', 'Bank Name', 'Bank Code', 'Sort Code'])
  filteredItems.value.forEach((bank, index) => {
    worksheet.addRow([index + 1, bank.bankName, bank.bankCodeName, bank.bankSortCode])
  })
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  }
  worksheet.columns.forEach(col => { col.width = 25 })
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'banks.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchBanks()
  document.title = 'View Banks'
})
</script>

<style scoped>
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.view-link {
  color: #428bca;
  font-weight: 500;
}

.view-link:hover {
  color: #2a6496;
  text-decoration: underline !important;
}
</style>
